import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@meno/ui/components/ui/button'
import { reactivateScreen } from '@meno/ui/lib/api'
import { screenDisplayStatus, type ScreenStatusLike } from '@meno/ui/components/shared/ScreenStatusBadge'

// Operator-facing surface for a screen whose license IS valid (renewed or
// perpetual) but that never automatically reconnected — the device orphaned
// on the old expiry, re-registered, and has been sitting there ever since
// because the renewal's auto-repair only fires at the exact moment an admin
// renews AND the device has already re-registered; miss that window and
// nothing else nudges it back (see devices.py register vs admin/routes/
// licenses.py:_sync_expires_to_screen). This is DELIBERATELY separate from
// ExpiredScreenBanner: that one covers a screen still waiting on an actual
// license renewal (nothing to reconnect to yet); this one covers a screen
// that's licensed and just needs the operator to click through.
//
// No pairing code needed — reactivateScreen resolves by this screen's own
// (already org-owned) id, and the device_token already bound to it is
// already the right one. See screens_pairing.py:reactivate_screen.
//
// Condition: unpaired, has been paired before (disconnect_at was set at
// least once), and is NOT currently expired — i.e. screenDisplayStatus
// resolves to anything but "expired" (offline/online/expiring all qualify;
// a screen mid-heartbeat-lag showing "online" while unpaired can't happen,
// effective_status already folds is_paired in, so this is realistically
// "offline").
type ReconnectPendingLike = ScreenStatusLike & {
  public_id?: string
}

export default function ReconnectPendingBanner({
  screen, onReactivated, compact = false,
}: {
  screen: ReconnectPendingLike
  // Refetch hook — the caller reloads the screen so the banner disappears
  // (is_paired flips true) without a full page reload.
  onReactivated?: () => void
  compact?: boolean
}) {
  const { t } = useTranslation()
  const [reactivating, setReactivating] = useState(false)

  const hasPriorHistory = screen.disconnect_at != null
  const isPaired = screen.is_paired !== false
  const isExpired = screenDisplayStatus(screen) === 'expired'
  // A DETACHED screen satisfies every other condition here — it is unpaired,
  // it kept its licence (detach deliberately does), and it is not expired — so
  // this banner used to appear on one and offer Reactivate. That action cannot
  // work: reactivate re-pairs the device_token already bound to the row and
  // relies on the same device_uid re-registering, and detach nulls both. The
  // operator was handed a button whose only possible outcome was an error.
  //
  // There is nothing to reconnect TO when no hardware is bound; attaching a
  // replacement is a relink, not a reactivation.
  const isDetached = screen.has_device === false
  if (isPaired || !hasPriorHistory || isExpired || isDetached) return null

  const reactivate = async () => {
    if (!screen.public_id || reactivating) return
    setReactivating(true)
    try {
      await reactivateScreen(screen.public_id)
      toast.success(t('screens.reactivateSuccess', 'Screen reactivated'))
      onReactivated?.()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || t('screens.reactivateFailed', 'Could not reactivate. Try again.'))
    } finally {
      setReactivating(false)
    }
  }

  return (
    <div className={compact
      ? 'min-w-0 max-w-md flex items-center gap-2 rounded-lg border border-amber-500/70 bg-amber-500/15 px-2.5 py-1.5 text-amber-700 dark:text-amber-300'
      : 'shrink-0 mx-[var(--space-l)] mt-[var(--space-l)] flex items-center gap-3 rounded-xl border border-amber-500 bg-amber-500 px-4 py-3 text-white'}>
      <RefreshCw className={compact ? 'w-4 h-4 shrink-0' : 'w-5 h-5 shrink-0'} />
      <p className={compact ? 'min-w-0 truncate text-xs font-semibold' : 'min-w-0 flex-1 text-sm font-semibold'}>
        {t('screens.reconnectPendingTitle', 'Ready to reconnect — license is active')}
      </p>
      <Button
        variant="outline"
        size="sm"
        onClick={reactivate}
        disabled={reactivating}
        className={compact
          ? 'h-7 gap-1.5 shrink-0 border-amber-500/50 bg-amber-500 text-white hover:bg-amber-600 hover:text-white'
          : 'gap-2 shrink-0 bg-white/15 border-white/40 text-white hover:bg-white/25 hover:text-white'}
      >
        {reactivating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        {t('screens.reactivate', 'Reactivate')}
      </Button>
    </div>
  )
}
